import React, { useEffect, useRef, useState, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import "./FormPreview.css";
import { PreviewWhiteHeading, PreviewWhiteSubHeading, SubHeading } from "../../../../Global/GlobalText";
import { GreyBackgroundButton, GreyBorderButton } from "../../../../Global/GlobalButton";
import { useSelector } from "react-redux";
import logoaward from "../../../../../Assets/logoaward.png";
import _ from "lodash";
import { EXCHNAGE_URL } from "../../../../../Url/Url";
import axios from "axios";
import { SelectBorder } from "../../../../Global/GlobalFormElement";
import { createSelector } from "reselect";
import { debounce } from "lodash";

const countries = [
  "United States", "Canada", "United Kingdom", "Australia", "India", "Germany",
  "France", "Italy", "Spain", "Mexico", "Japan", "South Korea", "Brazil",
  "China", "South Africa", "Russia", "Netherlands", "Sweden", "Switzerland", "Belgium"
];

const selectEntryFieldPayload = createSelector(
  [(state) => state.entry.fieldPayload],
  (fieldPayload) => fieldPayload
);

const FormPreview = ({ formSchema, showModal, handleClose, eventBanner, eventName, closingTime, closingDate, eventLogo }) => {
  const prevFormDataRef = useRef(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [relatedFieldState, setRelatedFieldState] = useState("");
  const [fieldSize, setFieldSize] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const eventIdGet = useSelector((state) => state?.users?.id);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAwardId, setSelectedAwardId] = useState();
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [timeLeft, setTimeLeft] = useState("");

  const eventId = String(eventIdGet);
  const entryFieldPayload = useSelector(selectEntryFieldPayload);

  // Initialize formData
  useEffect(() => {
    const initialData = formSchema.reduce((acc, field) => {
      acc[field.dataName] = {
        value: field.defaultValue || "",
        isConditionMet: true,
        isRequired: field.required || false,
        label: field.label,
        isVisible: field.visible || true,
        dataName: field.dataName || "",
        selectedLabelPosition: field.selectedLabelPosition || "",
      };
      return acc;
    }, {});

    setFormData((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(initialData)) {
        return initialData;
      }
      return prev;
    });
  }, [formSchema]);

  // Fetch categories
  const getCategories = async () => {
    setIsLoading(true);
    try {
      let apiUrl = `${EXCHNAGE_URL}/allAwards?eventId=${eventId}&sortOrder=${sortOrder}`;
      if (searchTerm) {
        apiUrl += `&search=${searchTerm}`;
      }
      const response = await axios.get(apiUrl, {
        headers: {
          "Content-Type": "Application/json",
          authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (response.status === 200) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCategories();
  }, [eventId, searchTerm, sortOrder]);

  // Calculate time left
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const closingDate2 = new Date(closingDate);
      const difference = closingDate2 - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        setTimeLeft(`${days}`);
      } else {
        setTimeLeft("Time Expired");
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);

    return () => clearInterval(timer);
  }, [closingDate]);

  // Memoized condition check functions
  const checkRequiredCondition = useMemo(
    () =>
      debounce(() => {
        if (!entryFieldPayload || !Array.isArray(entryFieldPayload)) return;
  
        const formDataByLabel = new Map(
          Object.values(formData).map((field) => [field.label, field])
        );
  
        let formUpdated = false;
        const updatedFormData = { ...formData };
  
        entryFieldPayload.forEach((fieldPayloadItem) => {
          const {
            requiredFieldValue = [],
            requiredConditionValue = [],
            requiredCondition = [],
            requiredCriteriaPattern = "", // e.g., "1 AND 2 AND 3" or "1 OR 2 OR 3" or "1 AND 8"
            dataName = "",
          } = fieldPayloadItem;
  
          if (
            !Array.isArray(requiredFieldValue) ||
            !Array.isArray(requiredConditionValue) ||
            !Array.isArray(requiredCondition) ||
            !requiredCriteriaPattern
          ) {
            return;
          }
  
          const relatedField = updatedFormData[dataName];
          if (!relatedField) return;
  
          // Parse the pattern into tokens (e.g., ["1", "AND", "2", "AND", "3"])
          const patternTokens = requiredCriteriaPattern.split(/\s+/);
          const conditionIndexes = patternTokens
            .map((token, idx) => (isNaN(token) ? null : parseInt(token) - 1))
            .filter((idx) => idx !== null);
  
          if (conditionIndexes.length === 0) return;
  
          let conditionMet = false;
  
          // Helper function to evaluate a single condition
          const evaluateCondition = (index) => {
            if (index < 0 || index >= requiredFieldValue.length) return false;
            const fieldLabel = requiredFieldValue[index];
            const condition = requiredCondition[index] || "Equals";
            const conditionValue = requiredConditionValue[index] || "";
            const fieldToCheck = formDataByLabel.get(fieldLabel);
            if (!fieldToCheck || !fieldToCheck.value) return false;
  
            switch (condition) {
              case "Equals":
                return fieldToCheck.value === conditionValue;
              case "contains":
                return fieldToCheck.value.includes(conditionValue);
              case "startsWith":
                return fieldToCheck.value.startsWith(conditionValue);
              case "endsWith":
                return fieldToCheck.value.endsWith(conditionValue);
              case "not-contains":
                return !fieldToCheck.value.includes(conditionValue);
              case "not-startsWith":
                return !fieldToCheck.value.startsWith(conditionValue);
              case "not-endsWith":
                return !fieldToCheck.value.endsWith(conditionValue);
              default:
                return fieldToCheck.value === conditionValue;
            }
          };
  
          // Evaluate the pattern
          if (patternTokens.includes("AND")) {
            // For AND, all conditions must be true
            conditionMet = conditionIndexes.every((index) => evaluateCondition(index));
          } else if (patternTokens.includes("OR")) {
            // For OR, at least one condition must be true
            conditionMet = conditionIndexes.some((index) => evaluateCondition(index));
          } else {
            // Single condition or invalid pattern, evaluate the first condition
            conditionMet = evaluateCondition(conditionIndexes[0]);
          }
  
          if (relatedField.isRequired !== conditionMet) {
            updatedFormData[dataName] = { ...relatedField, isRequired: conditionMet };
            formUpdated = true;
          }
        });
  
        if (formUpdated) {
          setFormData(updatedFormData);
        }
      }, 300),
    [formData]
  );
  
  const checkVisibilityCondition = useMemo(
    () =>
      debounce(() => {
        if (!entryFieldPayload || !Array.isArray(entryFieldPayload)) return;
  
        const formDataByDataName = new Map(
          Object.values(formData).map((field) => [field.dataName, field])
        );
  
        let formUpdated = false;
        const updatedFormData = { ...formData };
  
        entryFieldPayload.forEach((fieldPayloadItem) => {
          const {
            visible = true,
            visibleFieldValue = [],
            visibleConditionValue = [],
            visibleCondition = [],
            visibleCriteriaPattern = "", // e.g., "1 AND 2 AND 3" or "1 OR 2 OR 3" or "1 AND 8"
            dataName = "",
          } = fieldPayloadItem;
  
          const relatedField = updatedFormData[dataName];
          if (!relatedField) return;
  
          if (!visible) {
            if (relatedField.isVisible) {
              updatedFormData[dataName] = { ...relatedField, isVisible: false };
              formUpdated = true;
            }
            return;
          }
  
          if (
            !visibleFieldValue.length ||
            !visibleConditionValue.length ||
            !visibleCondition.length ||
            !visibleCriteriaPattern
          ) {
            return;
          }
  
          const patternTokens = visibleCriteriaPattern.split(/\s+/);
          const conditionIndexes = patternTokens
            .map((token, idx) => (isNaN(token) ? null : parseInt(token) - 1))
            .filter((idx) => idx !== null);
  
          if (conditionIndexes.length === 0) return;
  
          let shouldHide = true;
  
          const evaluateCondition = (index) => {
            if (index < 0 || index >= visibleFieldValue.length) return false;
            const fieldToCheck = formDataByDataName.get(visibleFieldValue[index]);
            const condition = visibleCondition[index] || "Equals";
            const valueToCheck = visibleConditionValue[index] || "";
            if (!fieldToCheck) return false;
  
            switch (condition) {
              case "Equals":
                return fieldToCheck.value === valueToCheck;
              case "contains":
                return fieldToCheck.value.includes(valueToCheck);
              case "startsWith":
                return fieldToCheck.value.startsWith(valueToCheck);
              case "endsWith":
                return fieldToCheck.value.endsWith(valueToCheck);
              case "not-contains":
                return !fieldToCheck.value.includes(valueToCheck);
              case "not-startsWith":
                return !fieldToCheck.value.startsWith(valueToCheck);
              case "not-endsWith":
                return !fieldToCheck.value.endsWith(valueToCheck);
              default:
                return fieldToCheck.value === valueToCheck;
            }
          };
  
          if (patternTokens.includes("AND")) {
            shouldHide = !conditionIndexes.every((index) => evaluateCondition(index));
          } else if (patternTokens.includes("OR")) {
            shouldHide = !conditionIndexes.some((index) => evaluateCondition(index));
          } else {
            shouldHide = !evaluateCondition(conditionIndexes[0]);
          }
  
          const newVisibility = !shouldHide;
          if (relatedField.isVisible !== newVisibility) {
            updatedFormData[dataName] = { ...relatedField, isVisible: newVisibility };
            formUpdated = true;
          }
        });
  
        if (formUpdated) {
          setFormData(updatedFormData);
        }
      }, 300),
    [formData]
  );

  const handlePosition = useMemo(
    () =>
      debounce(() => {
        if (!entryFieldPayload || !Array.isArray(entryFieldPayload)) return;

        let formUpdated = false;
        const updatedFormData = { ...formData };

        entryFieldPayload.forEach((entryFieldPayloadItem) => {
          const { selectedLabelPosition, dataName } = entryFieldPayloadItem;
          const fieldToCheck = updatedFormData[dataName];

          if (fieldToCheck && fieldToCheck.selectedLabelPosition !== selectedLabelPosition) {
            updatedFormData[dataName] = {
              ...fieldToCheck,
              selectedLabelPosition,
            };
            formUpdated = true;
          }
        });

        if (formUpdated) {
          setFormData(updatedFormData);
        }
      }, 300),
    [formData]
  );

  // Update fieldSize (only set once based on the first or a specific field)
  useEffect(() => {
    if (entryFieldPayload && Array.isArray(entryFieldPayload) && entryFieldPayload.length > 0) {
      // Use the first item's fieldSize or a specific logic to determine the desired fieldSize
      const { selectedFieldSize } = entryFieldPayload[0];
      if (selectedFieldSize !== fieldSize) {
        setFieldSize(selectedFieldSize);
      }
    }
  }, [entryFieldPayload]);

  // Run condition checks and position updates
  useEffect(() => {
    checkRequiredCondition();
    checkVisibilityCondition();
    handlePosition();
  }, [selectedCategoryName, entryFieldPayload, checkRequiredCondition, checkVisibilityCondition, handlePosition]);

  const handleInputChange = (dataName, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [dataName]: {
        ...prevData[dataName],
        value,
      },
    }));
  };

  const handleCategoryChange = (e, dataName, value) => {
    const selectedCategoryName = e.target.value;
    const selectedAwardId = e.target.options[e.target.selectedIndex].getAttribute("data-award-id");

    setSelectedCategoryName(selectedCategoryName);
    setSelectedAwardId(selectedAwardId);

    setFormData((prevData) => ({
      ...prevData,
      [dataName]: {
        ...prevData[dataName],
        value,
      },
    }));
  };

  const handleCheckboxChange = (fieldName, value) => {
    setFormData((prevData) => {
      const currentValue = prevData[fieldName]?.value || [];
      if (currentValue.includes(value)) {
        return {
          ...prevData,
          [fieldName]: {
            ...prevData[fieldName],
            value: currentValue.filter((v) => v !== value),
          },
        };
      } else {
        return {
          ...prevData,
          [fieldName]: {
            ...prevData[fieldName],
            value: [...currentValue, value],
          },
        };
      }
    });
  };

const renderField = (field) => {
  const isFieldError = errors[field.dataName];
  if (!formData[field.dataName]?.isConditionMet) return null;

  const requiredConditionMet = checkRequiredCondition(field);

  const relatedField = formData[field.dataName];

  const fieldClass = relatedField?.isVisible ? "show" : "hide";
  handlePosition(relatedField);
  const labelPositionLeft = relatedField?.selectedLabelPosition === "left";

  const fieldPayloadItem = entryFieldPayload?.find(
    (item) => item.dataName === field.dataName
  );
  const fieldWidth = fieldPayloadItem?.selectedFieldSize || "100%";

  const fieldStyle = {
    display: "flex",
    flexDirection: labelPositionLeft ? "row" : "column",
    alignItems: labelPositionLeft ? "center" : "flex-start",
  };
  const labelStyle = {
    marginRight: labelPositionLeft ? "10px" : "0",
    width: labelPositionLeft ? "120px" : "auto",
    textAlign: labelPositionLeft ? "left" : "center",
    minWidth: labelPositionLeft ? "120px" : "auto",
  };
  const inputStyle = {
    width: fieldWidth,
    marginLeft: labelPositionLeft ? "5rem" : "0",
  };

  const labelText = entryFieldPayload && entryFieldPayload.find(item => item.dataName === field.dataName)?.label || field.label;

  // ✅ Check if label contains HTML tags
  const containsHTML = /<[a-z][\s\S]*>/i.test(labelText);

  const hoverHint = (entryFieldPayload && entryFieldPayload.find((item) => item.dataName === field.dataName)?.hoverHint) || ""

  return (
    <div className={fieldClass} >
      <div key={field.dataName} className="form-field" style={fieldStyle}>
        <label style={labelStyle}>
          {/* ✅ Render HTML if contains tags, else plain text */}
          {containsHTML ? (
            <span 
              className="form-label-html"
              dangerouslySetInnerHTML={{ __html: labelText }} 
            />
          ) : (
            labelText
          )}
          {(
            formData[field.dataName]?.isRequired ||
            (formData[field.dataName]?.isRequired === false &&
              entryFieldPayload?.some(item =>
                item.dataName === field.dataName &&
                (!item.requiredConditionValue || item.requiredConditionValue.every(val => val.trim() === "")) &&
                item.required
              ))
          ) && (
            <span style={{ color: "red" }}>
              *
            </span>
          )}
          {hoverHint && (
            <span className="hover-hint-icon" title={hoverHint}>?</span>
          )}
        </label>
        {
          (() => {
            switch (field.type) {
              case "text":
                return (
                  <input
                    type="text"
                    placeholder={field.placeholder || ''}
                    value={formData[field.dataName]?.value || ""}
                    onChange={(e) => handleInputChange(field.dataName, e.target.value)}
                    required={requiredConditionMet || formData[field.dataName]?.isRequired}
                    style={inputStyle}
                  />
                );
              case "email":
                return (
                  <input
                    type="email"
                    placeholder={field.placeholder || ''}
                    value={formData[field.dataName]?.value || ""}
                    onChange={(e) => handleInputChange(field.dataName, e.target.value)}
                    required={requiredConditionMet || formData[field.dataName]?.isRequired}
                    style={inputStyle}
                  />
                );
              case "textarea":
                return (
                  <textarea
                    placeholder={field.placeholder || field.defaultValue || ''}
                    value={formData[field.dataName]?.value || ""}
                    onChange={(e) => handleInputChange(field.dataName, e.target.value)}
                    required={requiredConditionMet || formData[field.dataName]?.isRequired}
                    style={{ ...inputStyle, height: "100px" }}
                  />
                );
              case "date":
                return (
                  <input
                    type="date"
                    value={formData[field.dataName]?.value || ""}
                    onChange={(e) => handleInputChange(field.dataName, e.target.value)}
                    required={requiredConditionMet || formData[field.dataName]?.isRequired}
                    style={inputStyle}
                  />
                );
              case "country":
                return (
                  <select
                    value={formData[field.dataName]?.value || ""}
                    onChange={(e) => handleInputChange(field.dataName, e.target.value)}
                    required={requiredConditionMet || formData[field.dataName]?.isRequired}
                    style={inputStyle}
                  >
                    <option value="">Select a country</option>
                    {countries.map((country, index) => (
                      <option key={index} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                );
              case "file":
                return (
                  <input
                    type="file"
                    onChange={(e) => handleInputChange(field.dataName, e.target.files)}
                    required={requiredConditionMet || formData[field.dataName]?.isRequired}
                    style={inputStyle}
                  />
                );
              case "radio":
                const findRadioPayload = entryFieldPayload?.find((item) => item.dataName === field.dataName);
                return (
                  <>
                    {(findRadioPayload ? findRadioPayload.options : formSchema?.find(f => f.dataName === field.dataName)?.options || []).map((option, index) => (
                      <div key={index} className="sub_form_preview_radio">
                        <input
                          type="radio"
                          name={field.dataName}
                          value={option}
                          checked={formData[field.dataName]?.value === option}
                          onChange={() => handleInputChange(field.dataName, option)}
                          required={requiredConditionMet || formData[field.dataName]?.isRequired}
                          style={inputStyle}
                        />
                        <label>{option}</label>
                      </div>
                    ))}
                  </>
                );
              case "checkbox":
                const findCheckboxPayload = entryFieldPayload?.find((item) => item.dataName === field.dataName);
                return (
                  <>
                    {(findCheckboxPayload ? findCheckboxPayload.options : formSchema?.find(f => f.dataName === field.dataName)?.options || []).map((option, index) => (
                      <div key={index} className="sub_form_preview_radio">
                        <input
                          type="checkbox"
                          name={field.dataName}
                          value={option}
                          checked={formData[field.dataName]?.value?.includes(option)}
                          onChange={() => handleCheckboxChange(field.dataName, option)}
                          required={requiredConditionMet || formData[field.dataName]?.isRequired}
                          style={inputStyle}
                        />
                        <label>{option}</label>
                      </div>
                    ))}
                  </>
                );
              case "dropdown":
                if (field.label === "Category") {
                  return (
                    <SelectBorder
                      name="category"
                      value={selectedCategoryName}
                      onChange={(e) => handleCategoryChange(e, field.dataName, e.target.value)}
                    >
                      <option value="">Select a Category</option>
                      {categories.map((category) => (
                        <option
                          key={category.awardId}
                          data-award-id={category.awardId}
                          value={category.category_name}
                        >
                          {category.category_name}
                        </option>
                      ))}
                    </SelectBorder>
                  );
                }
                else {
                  const findDropdownPayload = entryFieldPayload?.find((item) => item.dataName === field.dataName);
                  return (
                    <select
                      value={formData[field.dataName]?.value || ""}
                      onChange={(e) => handleInputChange(field.dataName, e.target.value)}
                      required={requiredConditionMet || formData[field.dataName]?.isRequired}
                      style={inputStyle}
                    >
                      {(findDropdownPayload ? findDropdownPayload.options : formSchema?.find(f => f.dataName === field.dataName)?.options || []).map((option, index) => (
                        <option key={index} value={option}>{option}</option>
                      ))}
                    </select>
                  );
                }
              case "tel":
                return (
                  <input
                    type="tel"
                    placeholder={field.placeholder || "Enter phone number"}
                    value={formData[field.dataName]?.value || ""}
                    onChange={(e) => handleInputChange(field.dataName, e.target.value)}
                    required={requiredConditionMet || formData[field.dataName]?.isRequired}
                    style={inputStyle}
                  />
                );
              default:
                return null;
            }
          })()
        }
        {isFieldError && <p style={{ color: "red", fontSize: "12px" }}>{errors[field.dataName]}</p>}
      </div>
    </div>
  );
};

  const handleSubmit = (e) => {
    e.preventDefault();

    const formIsValid = Object.values(formData).every(field => {
      if (field.isRequired && !field.value) {
        console.log(`Field ${field.label} is required but empty`);
        return false;
      }
      return true;
    });

    if (formIsValid) {
      console.log('Form is valid, proceed with submission!');
    } else {
      console.log('Form is invalid');
    }
  };

  return (
    <Modal show={showModal} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <SubHeading className="entry_title_header">Entry Form</SubHeading>
      </Modal.Header>
      <Modal.Body>

        <div className="entry_form_heading">

        <img src={eventLogo} alt="logo" />

          <div className="entry_fashion_content">
            <PreviewWhiteHeading>{eventName}</PreviewWhiteHeading>

            <div className="entry_sub_fashion_content">
              <PreviewWhiteSubHeading>{timeLeft} Days Left</PreviewWhiteSubHeading>
              <div>
                <PreviewWhiteSubHeading>
                  Entries Deadline
                </PreviewWhiteSubHeading>
                <PreviewWhiteSubHeading>
                {closingDate}
                </PreviewWhiteSubHeading>
              </div>
            </div>
          </div>

        </div>



        <div class="form_preview_heading">
          <SubHeading>Entry Form</SubHeading>
        </div>

        <div className="form_preview_section">
          {formSchema.length === 0 ? (
            <p>Loading form...</p> // Display loading message if form schema is empty
          ) : (
            formSchema.map((field) => renderField(field))
          )}
          <div className="entry_form_preview_buttons">
            <GreyBorderButton onClick={handleClose}>
              Close
            </GreyBorderButton>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default FormPreview;
